import React, { useState, useEffect } from 'react';
import { theme, alpha } from '../../../theme';
import { X, User, Phone, Clock, Calendar, DollarSign, FileText } from 'lucide-react';
import {
  CreateFixedBookingRequest,
  UpdateFixedBookingRequest,
  FixedBooking,
  GridVenue,
  DAY_OF_WEEK_LABELS,
} from '../../../types/venue';

interface CreateFixedBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: CreateFixedBookingRequest | UpdateFixedBookingRequest,
    isEdit: boolean
  ) => Promise<{ success: boolean; error?: string }>;
  venues: GridVenue[];
  editingFixedBooking?: FixedBooking | null;
  existingFixedBookings?: FixedBooking[];
}

const CreateFixedBookingModal: React.FC<CreateFixedBookingModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  venues,
  editingFixedBooking,
  existingFixedBookings = [],
}) => {
  const isEditing = !!editingFixedBooking;

  const [formData, setFormData] = useState<CreateFixedBookingRequest>({
    venueId: '',
    customerName: '',
    customerPhone: '',
    dayOfWeek: 1, // Monday default
    startTime: '18:00',
    endTime: '19:00',
    agreedPrice: 0,
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    notes: '',
  });

  const [useNormalPrice, setUseNormalPrice] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate form when editing
  useEffect(() => {
    if (editingFixedBooking) {
      setUseNormalPrice(false);
      setFormData({
        venueId: editingFixedBooking.venueId,
        customerName: editingFixedBooking.customerName,
        customerPhone: editingFixedBooking.customerPhone || '',
        dayOfWeek: editingFixedBooking.dayOfWeek,
        startTime: editingFixedBooking.startTime,
        endTime: editingFixedBooking.endTime,
        agreedPrice: editingFixedBooking.agreedPrice,
        startDate: editingFixedBooking.startDate,
        endDate: editingFixedBooking.endDate || '',
        notes: editingFixedBooking.notes || '',
      });
    } else {
      // Reset form for new
      setUseNormalPrice(true);
      setFormData({
        venueId: venues.length === 1 ? venues[0].id : '',
        customerName: '',
        customerPhone: '',
        dayOfWeek: 1,
        startTime: '18:00',
        endTime: '19:00',
        agreedPrice: 0,
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        notes: '',
      });
    }
  }, [editingFixedBooking, venues, isOpen]);

  // Compute normal price from venue rate
  const computeNormalPrice = (): number => {
    if (!formData.venueId || !formData.startTime || !formData.endTime) return 0;
    const venue = venues.find((v) => v.id === formData.venueId);
    if (!venue) return 0;
    const [startH, startM] = formData.startTime.split(':').map(Number);
    const [endH, endM] = formData.endTime.split(':').map(Number);
    const durationHours = (endH * 60 + endM - startH * 60 - startM) / 60;
    return durationHours > 0 ? durationHours * venue.hourlyRate : 0;
  };

  // Auto-set price when "usar precio normal" is on or venue/time changes
  useEffect(() => {
    if (useNormalPrice && !isEditing) {
      const normalPrice = computeNormalPrice();
      if (normalPrice > 0) {
        setFormData((prev) => ({ ...prev, agreedPrice: normalPrice }));
      }
    }
  }, [formData.venueId, formData.startTime, formData.endTime, useNormalPrice, isEditing, venues]);

  // Build occupied hours for the selected venue + day (only active/paused, exclude the one being edited)
  const occupiedSlots = existingFixedBookings
    .filter(
      (fb) =>
        fb.venueId === formData.venueId &&
        fb.dayOfWeek === formData.dayOfWeek &&
        (fb.status === 'ACTIVE' || fb.status === 'PAUSED') &&
        (!editingFixedBooking || fb.id !== editingFixedBooking.id)
    )
    .map((fb) => ({ start: fb.startTime, end: fb.endTime }));

  // Generate all hour slots (e.g., "08:00" to "23:00")
  const allTimeSlots: string[] = [];
  for (let h = 8; h <= 23; h++) {
    allTimeSlots.push(`${String(h).padStart(2, '0')}:00`);
  }

  // Filter available start times: a start time is available if it doesn't fall inside any occupied slot
  const availableStartTimes = allTimeSlots.filter((time) =>
    !occupiedSlots.some((slot) => time >= slot.start && time < slot.end)
  );

  // Filter available end times: must be after the selected start, and not overlap occupied slots between start and end
  const availableEndTimes = allTimeSlots
    .filter((time) => time > formData.startTime)
    .filter((time) => {
      // Check no occupied slot falls between formData.startTime and this end time
      return !occupiedSlots.some(
        (slot) => slot.start >= formData.startTime && slot.start < time
      );
    });

  // Sync formData times to first available slot when the current value is not in the available list
  useEffect(() => {
    if (isEditing) return;
    let newStart = formData.startTime;
    let newEnd = formData.endTime;
    let changed = false;

    if (availableStartTimes.length > 0 && !availableStartTimes.includes(formData.startTime)) {
      newStart = availableStartTimes[0];
      changed = true;
    }

    // Recompute available end times for the (possibly new) start
    const endTimesForStart = allTimeSlots
      .filter((time) => time > newStart)
      .filter((time) => !occupiedSlots.some((slot) => slot.start >= newStart && slot.start < time));

    if (endTimesForStart.length > 0 && (changed || !endTimesForStart.includes(formData.endTime))) {
      newEnd = endTimesForStart[0];
      changed = true;
    }

    if (changed) {
      setFormData((prev) => ({ ...prev, startTime: newStart, endTime: newEnd }));
    }
  }, [formData.venueId, formData.dayOfWeek, isOpen, isEditing, existingFixedBookings]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.customerName.trim()) {
      setError('Ingrese el nombre del cliente');
      return;
    }

    if (!formData.venueId) {
      setError('Seleccione una cancha');
      return;
    }

    setLoading(true);

    let submitData: CreateFixedBookingRequest | UpdateFixedBookingRequest;

    if (isEditing) {
      // Only send editable fields for update
      submitData = {
        customerName: formData.customerName,
        customerPhone: formData.customerPhone || undefined,
        agreedPrice: formData.agreedPrice,
        endDate: formData.endDate || undefined,
        notes: formData.notes || undefined,
      };
    } else {
      submitData = {
        ...formData,
        customerPhone: formData.customerPhone || undefined,
        endDate: formData.endDate || undefined,
        notes: formData.notes || undefined,
      };
    }

    const result = await onSubmit(submitData, isEditing);
    setLoading(false);

    if (result.success) {
      onClose();
    } else {
      setError(result.error || 'Error al guardar turno fijo');
    }
  };

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
    border: `1px solid ${theme.colors.border}`,
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${theme.colors.border}`,
    backgroundColor: '#D4AF3720', // Gold tint for fixed bookings
  };

  const titleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: theme.fonts.sizeSubtitle,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const formStyle: React.CSSProperties = {
    padding: '24px',
  };

  const fieldStyle: React.CSSProperties = {
    marginBottom: '20px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.textSecondary,
    marginBottom: '8px',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: theme.borderRadius.input,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    fontSize: theme.fonts.sizeBody,
    outline: 'none',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    cursor: 'pointer',
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '16px',
  };

  const halfFieldStyle: React.CSSProperties = {
    flex: 1,
  };

  const errorStyle: React.CSSProperties = {
    backgroundColor: `${alpha('danger', '20')}`,
    color: theme.colors.danger,
    padding: '12px 16px',
    borderRadius: theme.borderRadius.input,
    fontSize: theme.fonts.sizeSmall,
    marginBottom: '16px',
  };

  const footerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: `1px solid ${theme.colors.border}`,
  };

  const buttonStyle: React.CSSProperties = {
    padding: '12px 24px',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s',
  };

  const cancelButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: 'transparent',
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.text,
  };

  const submitButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#D4AF37', // Gold for fixed bookings
    border: 'none',
    color: '#000',
  };

  const helpTextStyle: React.CSSProperties = {
    fontSize: '12px',
    color: theme.colors.textSecondary,
    marginTop: '6px',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={titleStyle}>
            <span style={{ fontSize: '24px' }}>🔄</span>
            <span>{isEditing ? 'Editar Turno Fijo' : 'Nuevo Turno Fijo'}</span>
          </div>
          <button style={closeButtonStyle} onClick={onClose}>
            <X size={24} color={theme.colors.textSecondary} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          {error && <div style={errorStyle}>{error}</div>}

          {/* Venue Select - Only for new */}
          {!isEditing && (
            <div style={fieldStyle}>
              <label style={labelStyle}>
                <Calendar size={16} />
                Cancha *
              </label>
              <select
                style={selectStyle}
                value={formData.venueId}
                onChange={(e) => setFormData({ ...formData, venueId: e.target.value, ...(useNormalPrice ? {} : { agreedPrice: 0 }) })}
                required
              >
                <option value="">Seleccionar cancha</option>
                {venues.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name} ({v.venueType.replace('FUTBOL_', 'F')}) - ${v.hourlyRate}/h
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Day of Week - Only for new */}
          {!isEditing && (
            <div style={fieldStyle}>
              <label style={labelStyle}>
                <Calendar size={16} />
                Día de la Semana *
              </label>
              <select
                style={selectStyle}
                value={formData.dayOfWeek}
                onChange={(e) =>
                  setFormData({ ...formData, dayOfWeek: Number(e.target.value) })
                }
                required
              >
                {DAY_OF_WEEK_LABELS.map((day, index) => (
                  <option key={index} value={index}>
                    {day}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Time Range - Only for new */}
          {!isEditing && (
            <div style={rowStyle}>
              <div style={halfFieldStyle}>
                <label style={labelStyle}>
                  <Clock size={16} />
                  Hora Inicio *
                </label>
                <select
                  style={selectStyle}
                  value={formData.startTime}
                  onChange={(e) => {
                    const newStart = e.target.value;
                    // Auto-adjust endTime to +1h if current endTime is invalid
                    const nextHour = `${String(Number(newStart.split(':')[0]) + 1).padStart(2, '0')}:00`;
                    const newEnd = formData.endTime > newStart ? formData.endTime : nextHour;
                    setFormData({ ...formData, startTime: newStart, endTime: newEnd, ...(useNormalPrice ? {} : { agreedPrice: 0 }) });
                  }}
                  required
                >
                  {availableStartTimes.length === 0 ? (
                    <option value="">No hay horarios disponibles</option>
                  ) : (
                    availableStartTimes.map((time) => (
                      <option key={time} value={time}>{time}</option>
                    ))
                  )}
                </select>
              </div>
              <div style={halfFieldStyle}>
                <label style={labelStyle}>
                  <Clock size={16} />
                  Hora Fin *
                </label>
                <select
                  style={selectStyle}
                  value={formData.endTime}
                  onChange={(e) =>
                    setFormData({ ...formData, endTime: e.target.value, ...(useNormalPrice ? {} : { agreedPrice: 0 }) })
                  }
                  required
                >
                  {availableEndTimes.length === 0 ? (
                    <option value="">Seleccioná hora inicio</option>
                  ) : (
                    availableEndTimes.map((time) => (
                      <option key={time} value={time}>{time}</option>
                    ))
                  )}
                </select>
              </div>
            </div>
          )}

          {/* Customer Name */}
          <div style={fieldStyle}>
            <label style={labelStyle}>
              <User size={16} />
              Nombre del Cliente *
            </label>
            <input
              type="text"
              style={inputStyle}
              value={formData.customerName}
              onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
              placeholder="Nombre completo"
              required
            />
          </div>

          {/* Phone */}
          <div style={fieldStyle}>
            <label style={labelStyle}>
              <Phone size={16} />
              Teléfono (opcional)
            </label>
            <input
              type="tel"
              style={inputStyle}
              value={formData.customerPhone}
              onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
              placeholder="+54 11 1234-5678"
            />
          </div>

          {/* Price */}
          <div style={fieldStyle}>
            <label style={labelStyle}>
              <DollarSign size={16} />
              Precio Acordado *
            </label>
            {!isEditing && (
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '10px',
                cursor: 'pointer',
                fontSize: theme.fonts.sizeSmall,
                color: theme.colors.text,
              }}>
                <input
                  type="checkbox"
                  checked={useNormalPrice}
                  onChange={(e) => {
                    setUseNormalPrice(e.target.checked);
                    if (!e.target.checked) {
                      setFormData((prev) => ({ ...prev, agreedPrice: prev.agreedPrice }));
                    }
                  }}
                  style={{ accentColor: '#D4AF37', width: '18px', height: '18px' }}
                />
                Usar precio normal de la cancha
              </label>
            )}
            <input
              type="number"
              style={{
                ...inputStyle,
                ...(useNormalPrice && !isEditing ? { opacity: 0.6 } : {}),
              }}
              value={formData.agreedPrice}
              onChange={(e) =>
                setFormData({ ...formData, agreedPrice: Number(e.target.value) })
              }
              placeholder="Precio por turno"
              step="0.01"
              min="0"
              required
              disabled={useNormalPrice && !isEditing}
            />
            <div style={helpTextStyle}>
              {useNormalPrice && !isEditing
                ? 'Se usa el precio normal de la cancha. Desmarcá para ingresar un precio especial.'
                : 'Precio especial acordado con el cliente para este turno fijo semanal'}
            </div>
          </div>

          {/* Start Date - Only for new */}
          {!isEditing && (
            <div style={fieldStyle}>
              <label style={labelStyle}>
                <Calendar size={16} />
                Fecha de Inicio *
              </label>
              <input
                type="date"
                style={{ ...inputStyle, colorScheme: 'dark' }}
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                required
              />
              <div style={helpTextStyle}>
                Las reservas se generarán automáticamente desde esta fecha
              </div>
            </div>
          )}

          {/* End Date */}
          <div style={fieldStyle}>
            <label style={labelStyle}>
              <Calendar size={16} />
              Fecha de Fin (opcional)
            </label>
            <input
              type="date"
              style={{ ...inputStyle, colorScheme: 'dark' }}
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            />
            <div style={helpTextStyle}>
              Dejar vacío para turno fijo indefinido
            </div>
          </div>

          {/* Notes */}
          <div style={fieldStyle}>
            <label style={labelStyle}>
              <FileText size={16} />
              Notas (opcional)
            </label>
            <textarea
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Observaciones adicionales..."
            />
          </div>
        </form>

        <div style={footerStyle}>
          <button type="button" style={cancelButtonStyle} onClick={onClose}>
            Cancelar
          </button>
          <button
            type="submit"
            style={submitButtonStyle}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Guardando...' : isEditing ? 'Guardar Cambios' : 'Crear Turno Fijo'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateFixedBookingModal;
